export class GoogleAuthError extends Error { }
export class GoogleAuthPopupBlockedError extends GoogleAuthError { }
export class GoogleAuthFlowWasInterruptedError extends GoogleAuthError { }

class SelfClosingWindow extends Window {
    public lastSearch: string
}

async function openWindowAndWaitForItToBeClosed(url: string): Promise<URLSearchParams> {
    const newWindow = (window.open(url, "_blank") as SelfClosingWindow);
    if (!newWindow) {
        throw new GoogleAuthPopupBlockedError();
    }

    while (!newWindow.closed) {
        await new Promise(resolve => setTimeout(resolve, 500));
    }

    return new URLSearchParams(newWindow.lastSearch);
}

/**
 * Grants our app offline access for the given scope, executes the redirect ouath flow in a separate window
 * and saves the tokens
 */
export async function grantOfflineAccess(scope: string, userUid: string): Promise<void> {
    const url = `${window.location.origin}/google/auth` +
        `?scope=${encodeURIComponent(scope)}` +
        `&continueUrl=${encodeURIComponent("/close")}` +
        `&userUid=${encodeURIComponent(userUid)}` +
        "&requestOfflineAccess=true";

    const lastSearchParams = await openWindowAndWaitForItToBeClosed(url);

    if (lastSearchParams.has("google_auth_error")) {
        throw new GoogleAuthError(lastSearchParams.get("google_auth_error"));
    }

    if (lastSearchParams.get("google_auth_success") !== "true") {
        throw new GoogleAuthFlowWasInterruptedError();
    }
}

/**
 * Does google sign in flow using redirect
 */
export function authenticate(scope: string, continueUrl: string): void {
    window.location.href = `/google/auth?scope=${encodeURIComponent(scope)}&continueUrl=${encodeURIComponent(continueUrl)}`;
}

/**
 * Does google sign in flow using redirect in a separate window (faux popup), returns id token
 */
export async function authenticateWithPopup(scope: string): Promise<string> {
    const url = `${window.location.origin}/google/auth` +
        `?scope=${encodeURIComponent(scope)}` +
        `&continueUrl=${encodeURIComponent("/close")}`;

    const lastSearchParams = await openWindowAndWaitForItToBeClosed(url);

    if (lastSearchParams.has("google_auth_error")) {
        throw new GoogleAuthError(lastSearchParams.get("google_auth_error"));
    }

    if (lastSearchParams.get("google_auth_success") !== "true") {
        throw new GoogleAuthFlowWasInterruptedError();
    }

    return lastSearchParams.get("google_auth_id_token");
}
